import { useCallback, useEffect, useMemo, useState } from 'react';

import { VideoPreview } from '../../../components/VideoPreview';
import {
  retryFailedChecks,
  runEnvironmentChecks,
} from '../../../modules/environment-checker';
import { inspectActiveVideoTrack } from '../../../modules/environment-checker/checks/webcam-device-utils';
import { EnvironmentCheckReport } from '../../../modules/environment-checker/types';
import {
  CheckItem,
  CheckItemSkeleton,
} from '../../../modules/environment-checker/ui/CheckItem';
import { InstructionsPanel } from '../../../modules/environment-checker/ui/InstructionsPanel';
import {
  Certification,
  CertificationType,
} from '../../../types/certifications/Certification';
import { typeTranslate } from '../../../utils/type-translate';
import { resolveMonitoringRuntimeConfig } from '../config/monitoringConfig';
import { MediaValidationStatus } from '../guard/types';
import {
  MIN_SAFE_LUMINANCE,
  measureStreamLuminance,
} from '../utils/checkLighting';
import {
  EXAM_AUDIO_CONSTRAINTS,
  EXAM_VIDEO_CONSTRAINTS,
  EXAM_VIDEO_CONSTRAINTS_RELAXED,
} from '../utils/mediaConstraints';

interface PreparationApprovalState {
  mediaPermission: MediaValidationStatus;
  monitoringReady: boolean;
  monitoringType: CertificationType | null;
}

interface PreparationScreenProps {
  candidateId: string;
  certificationLogId: string;
  certification: Certification;
  selectedMonitoringType: CertificationType | null;
  stepLabel?: string;
  onMonitoringTypeChange: (type: CertificationType) => void;
  onApproved: (
    report: EnvironmentCheckReport,
    state: PreparationApprovalState,
  ) => void;
  onBack?: () => void;
  onScreenShareStream?: (stream: MediaStream | null) => void;
}

type ScreenState = 'idle' | 'running' | 'done';

const manualFallbackItems = [
  'Estou usando apenas um monitor para a prova.',
  'Minha webcam e meu microfone são físicos e estão operacionais.',
  'Não há compartilhamento de tela, acesso remoto ou projeção ativa.',
  'Desativei extensões e ferramentas auxiliares não permitidas.',
];

function requiresCameraMonitoring(monitoringType?: CertificationType | null) {
  return monitoringType === 'video' || monitoringType === 'video_liveness';
}

function getPreparationState(
  monitoringType: CertificationType | null,
  mediaPermission: MediaValidationStatus,
): PreparationApprovalState {
  if (requiresCameraMonitoring(monitoringType)) {
    return {
      mediaPermission,
      monitoringReady: mediaPermission === 'preview_ready',
      monitoringType,
    };
  }

  if (monitoringType === 'fiscal') {
    return {
      mediaPermission: 'idle',
      monitoringReady: false,
      monitoringType,
    };
  }

  return {
    mediaPermission: 'idle',
    monitoringReady: true,
    monitoringType,
  };
}

function sortMonitoringTypes(types: CertificationType[]) {
  if (!types.includes('video')) return types.filter(type => type !== 'select');

  const withoutSelect = types.filter(type => type !== 'select');
  const next = Array.from(withoutSelect);
  const videoIndex = next.indexOf('video');
  next.splice(videoIndex, 1);
  next.unshift('video');
  return next;
}

export function PreparationScreen({
  candidateId,
  certificationLogId,
  certification,
  selectedMonitoringType,
  stepLabel = 'Etapa 2 de 2',
  onMonitoringTypeChange,
  onApproved,
  onBack,
  onScreenShareStream,
}: PreparationScreenProps) {
  const [hasStarted, setHasStarted] = useState(false);
  const [state, setState] = useState<ScreenState>('idle');
  const [report, setReport] = useState<EnvironmentCheckReport | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [browserFallback, setBrowserFallback] = useState(false);
  const [previewStream, setPreviewStream] = useState<MediaStream | null>(null);
  const [mediaPermission, setMediaPermission] =
    useState<MediaValidationStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [lightingWarning, setLightingWarning] = useState<string | null>(null);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const [manualChecklist, setManualChecklist] = useState<boolean[]>(
    manualFallbackItems.map(() => false),
  );

  const resolvedMonitoringType = useMemo<CertificationType | null>(() => {
    if (certification.monitoring === 'select') return selectedMonitoringType;
    return certification.monitoring;
  }, [certification.monitoring, selectedMonitoringType]);
  const {
    environmentCheckTimeoutMs,
    monitoringCheckSecondScreenEnabled,
    monitoringCheckVirtualWebcamEnabled,
    monitoringCheckSuspiciousExtensionsEnabled,
    screenShareEnabled,
  } = useMemo(
    () => resolveMonitoringRuntimeConfig(certification),
    [certification],
  );

  const checkPolicy = useMemo(
    () => ({
      secondScreenEnabled: monitoringCheckSecondScreenEnabled,
      virtualWebcamEnabled: monitoringCheckVirtualWebcamEnabled,
      suspiciousExtensionsEnabled: monitoringCheckSuspiciousExtensionsEnabled,
      screenShareEnabled,
    }),
    [
      monitoringCheckSecondScreenEnabled,
      monitoringCheckVirtualWebcamEnabled,
      monitoringCheckSuspiciousExtensionsEnabled,
      screenShareEnabled,
    ],
  );

  const monitoringOptions = useMemo(
    () => sortMonitoringTypes(certification.monitoring_select ?? []),
    [certification.monitoring_select],
  );

  const needsCamera = requiresCameraMonitoring(resolvedMonitoringType);
  const isFiscalMonitoring = resolvedMonitoringType === 'fiscal';
  const visibleChecks = report?.checks.filter(
    check => check.status !== 'skipped',
  );
  const blockingChecks = visibleChecks?.filter(
    check => check.status === 'failed',
  );
  const warningChecks = visibleChecks?.filter(
    check => check.status === 'warning',
  );
  const isEnvironmentApproved = report?.overallStatus === 'approved';
  const isMediaReady = !needsCamera || mediaPermission === 'preview_ready';
  const canContinue = Boolean(isEnvironmentApproved && isMediaReady);
  const manualChecklistComplete = manualChecklist.every(Boolean);
  let mediaStatusLabel = 'Não aplicável';
  if (mediaPermission === 'preview_ready') {
    mediaStatusLabel = 'Pronta';
  } else if (needsCamera) {
    mediaStatusLabel = 'Pendente';
  }
  const stateTitle = useMemo(() => {
    if (state === 'done') return 'Preparação concluída';
    if (hasStarted) return 'Preparação em andamento';
    return 'Aguardando início';
  }, [hasStarted, state]);
  const stateDescription = useMemo(() => {
    if (state === 'done') {
      return 'Validação finalizada. Revise os resultados e avance.';
    }

    return 'A câmera só será solicitada após seu clique em iniciar.';
  }, [state]);
  const resolvedStateDescription =
    isFiscalMonitoring && state !== 'done'
      ? 'Nesta etapa, validamos o ambiente antes da autorização fiscal.'
      : stateDescription;

  useEffect(() => {
    return () => {
      previewStream?.getTracks().forEach(track => track.stop());
    };
  }, [previewStream]);

  const requestMedia = useCallback(async (): Promise<MediaValidationStatus> => {
    setMediaPermission('requesting');
    setErrorMessage(null);
    setLightingWarning(null);

    try {
      previewStream?.getTracks().forEach(track => track.stop());

      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: EXAM_AUDIO_CONSTRAINTS,
          video: EXAM_VIDEO_CONSTRAINTS,
        });
      } catch (constraintError) {
        const err = constraintError as DOMException;
        if (err?.name === 'OverconstrainedError') {
          // Webcam rejeitou frameRate.max=30; tenta com constraints relaxadas
          // mantendo os ideais (preferência por 30fps/720p).
          stream = await navigator.mediaDevices.getUserMedia({
            audio: EXAM_AUDIO_CONSTRAINTS,
            video: EXAM_VIDEO_CONSTRAINTS_RELAXED,
          });
        } else {
          throw constraintError;
        }
      }

      setPreviewStream(stream);
      setMediaPermission('granted');

      const videoTracks = stream.getVideoTracks();
      const audioTracks = stream.getAudioTracks();
      const activeVideoTrack = videoTracks[0];

      if (!activeVideoTrack || audioTracks.length === 0) {
        setMediaPermission('error');
        setErrorMessage(
          'A permissão foi concedida, mas câmera e áudio não ficaram prontos.',
        );
        return 'error';
      }

      const devices = navigator.mediaDevices?.enumerateDevices
        ? await navigator.mediaDevices.enumerateDevices()
        : [];
      const inspection = await inspectActiveVideoTrack(
        activeVideoTrack,
        devices,
      );

      if (
        monitoringCheckVirtualWebcamEnabled &&
        inspection.activeDeviceIsVirtual === true
      ) {
        setMediaPermission('error');
        setErrorMessage(
          `Câmera virtual detectada: "${
            inspection.activeDeviceLabel || 'Câmera virtual'
          }". Selecione uma webcam física para continuar.`,
        );
        return 'error';
      }

      // Pré-checagem de iluminação: não-bloqueante. Falhas silenciosas não
      // podem interromper o fluxo de preparação.
      const luminance = await measureStreamLuminance(stream);
      if (typeof luminance === 'number' && luminance < MIN_SAFE_LUMINANCE) {
        setLightingWarning(
          'Ambiente muito escuro. Melhore a iluminação para maior precisão do monitoramento.',
        );
      } else {
        setLightingWarning(null);
      }

      setMediaPermission('preview_ready');
      return 'preview_ready';
    } catch (error) {
      const nextError = error as DOMException;
      setPreviewStream(null);

      if (
        nextError?.name === 'NotAllowedError' ||
        nextError?.name === 'PermissionDeniedError'
      ) {
        setMediaPermission('denied');
        setErrorMessage(
          'Permissão negada para câmera e áudio. Libere o acesso e tente novamente.',
        );
        return 'denied';
      }

      setMediaPermission('error');
      setErrorMessage(
        'Falha ao iniciar câmera e áudio. Feche outros apps e tente novamente.',
      );
      return 'error';
    }
  }, [monitoringCheckVirtualWebcamEnabled, previewStream]);

  const runChecks = useCallback(async () => {
    setState('running');
    setSelectionError(null);

    if (needsCamera) {
      await requestMedia();
    } else {
      setMediaPermission('idle');
    }

    try {
      const result = await runEnvironmentChecks({
        candidateId,
        certificationLogId,
        timeoutMs: environmentCheckTimeoutMs,
        policy: checkPolicy,
      });

      setReport(result);
      setBrowserFallback(false);
      setState('done');

      const screenShareResult = result.checks.find(
        c => c.checkId === 'screen_share' && c.status === 'passed',
      );
      const screenShareStream =
        (screenShareResult?._streamRef as MediaStream) ?? null;
      onScreenShareStream?.(screenShareStream);
    } catch {
      setBrowserFallback(true);
      setState('done');
    }
  }, [
    candidateId,
    certificationLogId,
    checkPolicy,
    environmentCheckTimeoutMs,
    needsCamera,
    onScreenShareStream,
    requestMedia,
  ]);

  const handleStart = useCallback(() => {
    setHasStarted(true);

    if (!resolvedMonitoringType) {
      setSelectionError('Selecione o tipo de acompanhamento para continuar.');
      return;
    }

    runChecks().catch(() => undefined);
  }, [resolvedMonitoringType, runChecks]);

  const handleRetry = useCallback(async () => {
    if (!resolvedMonitoringType) {
      setSelectionError('Selecione o tipo de acompanhamento para continuar.');
      return;
    }

    if (needsCamera && mediaPermission !== 'preview_ready') {
      await requestMedia();
    }

    if (!report) {
      await runChecks();
      return;
    }

    setRetrying(true);
    try {
      const retryResult = await retryFailedChecks(report, {
        candidateId,
        certificationLogId,
        timeoutMs: environmentCheckTimeoutMs,
        policy: checkPolicy,
      });
      setReport(retryResult);

      const screenShareResult = retryResult.checks.find(
        c => c.checkId === 'screen_share' && c.status === 'passed',
      );
      const screenShareStream =
        (screenShareResult?._streamRef as MediaStream) ?? null;
      onScreenShareStream?.(screenShareStream);
    } finally {
      setRetrying(false);
    }
  }, [
    candidateId,
    certificationLogId,
    checkPolicy,
    environmentCheckTimeoutMs,
    mediaPermission,
    needsCamera,
    onScreenShareStream,
    report,
    requestMedia,
    resolvedMonitoringType,
    runChecks,
  ]);

  const handleContinue = useCallback(() => {
    if (!report || !canContinue || !resolvedMonitoringType) return;

    onApproved(
      report,
      getPreparationState(resolvedMonitoringType, mediaPermission),
    );
  }, [
    canContinue,
    mediaPermission,
    onApproved,
    report,
    resolvedMonitoringType,
  ]);

  const handleManualApproval = useCallback(() => {
    if (!resolvedMonitoringType) return;

    const timestamp = new Date().toISOString();
    const manualReport: EnvironmentCheckReport = {
      candidateId,
      certificationLogId,
      overallStatus: 'approved',
      checks: manualFallbackItems.map((item, index) => ({
        checkId: `manual_fallback_${index}`,
        checkName: `Confirmacao manual ${index + 1}`,
        status: 'passed',
        details: item,
        timestamp,
      })),
      startedAt: timestamp,
      completedAt: timestamp,
      durationMs: 0,
      browserInfo: {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
      },
    };

    onApproved(
      manualReport,
      getPreparationState(resolvedMonitoringType, mediaPermission),
    );
  }, [
    candidateId,
    certificationLogId,
    mediaPermission,
    onApproved,
    resolvedMonitoringType,
  ]);

  if (browserFallback) {
    const canManualContinue = manualChecklistComplete && isMediaReady;

    return (
      <div className="min-h-screen bg-slate-100 px-4 py-8">
        <div className="mx-auto max-w-4xl rounded-3xl border border-slate-200 bg-white p-8 shadow-xl">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">
            {stepLabel}
          </p>
          <h1 className="mt-3 text-3xl font-bold text-slate-900">
            Preparação pré-prova
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-600">
            Seu navegador não suportou toda a validação automática. Confirme o
            checklist manual para seguir com segurança.
          </p>

          <div className="mt-6 grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
            <div className="space-y-4 rounded-2xl border border-amber-200 bg-amber-50 p-5">
              <InstructionsPanel failedChecks={[]} manualFallback />
              {manualFallbackItems.map((item, index) => (
                <label
                  key={item}
                  className="flex items-start gap-3 rounded-xl border border-white/80 bg-white/70 px-4 py-3 text-sm text-slate-700"
                >
                  <input
                    type="checkbox"
                    checked={manualChecklist[index]}
                    onChange={event => {
                      setManualChecklist(current =>
                        current.map((value, currentIndex) =>
                          currentIndex === index ? event.target.checked : value,
                        ),
                      );
                    }}
                    className="mt-1 rounded border-slate-300 text-blue-700 focus:ring-blue-500"
                  />
                  <span>{item}</span>
                </label>
              ))}
            </div>

            <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5">
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                {needsCamera
                  ? 'A câmera precisa permanecer aprovada para continuar.'
                  : 'Validação manual pronta para seguir.'}
              </div>
              <button
                type="button"
                onClick={handleManualApproval}
                disabled={!canManualContinue}
                className={`w-full rounded-xl bg-blue-700 px-4 py-3 text-sm font-semibold text-white transition hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-40 ${
                  canManualContinue ? 'cta-guidance cta-guidance-blue' : ''
                }`}
              >
                Confirmar checklist e avançar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-8">
      <div className="mx-auto max-w-5xl rounded-3xl border border-slate-200 bg-white p-8 shadow-xl">
        <div className="flex flex-col gap-6 border-b border-slate-200 pb-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-3">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">
              {stepLabel}
            </p>
            <h1 className="text-3xl font-bold text-slate-900">
              Preparação pré-prova
            </h1>
            <p className="max-w-2xl text-sm text-slate-600">
              Após revisar os requisitos, valide ambiente e dispositivos para
              liberar a entrada na prova.
            </p>
          </div>

          <div className="rounded-2xl bg-slate-900 p-5 text-white">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-400">
              Estado atual
            </p>
            <p className="mt-2 text-lg font-semibold">{stateTitle}</p>
            <p className="mt-2 max-w-xs text-sm text-slate-300">
              {resolvedStateDescription}
            </p>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
          <div className="min-w-0 space-y-4">
            {certification.monitoring === 'select' && (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                <label
                  htmlFor="preparation-monitoring-type"
                  className="text-sm font-semibold text-slate-900"
                >
                  Tipo de acompanhamento
                </label>
                <select
                  id="preparation-monitoring-type"
                  value={selectedMonitoringType ?? ''}
                  onChange={event =>
                    onMonitoringTypeChange(
                      event.target.value as CertificationType,
                    )
                  }
                  className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 focus:border-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-700"
                >
                  <option value="">Selecione uma opção</option>
                  {monitoringOptions.map(type => (
                    <option key={type} value={type}>
                      {typeTranslate[type] ?? type}
                    </option>
                  ))}
                </select>
                {selectionError && (
                  <p className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {selectionError}
                  </p>
                )}
              </div>
            )}

            {!hasStarted && (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                <p className="text-sm text-slate-700">
                  Inicie a preparação para rodar as checagens do ambiente e
                  liberar o avanço para a prova.
                </p>
              </div>
            )}

            {state === 'running' && (
              <div
                className="space-y-3"
                role="status"
                aria-label="Preparando ambiente"
              >
                {Array.from({ length: 4 }).map((_, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <CheckItemSkeleton key={index} />
                ))}
              </div>
            )}

            {state === 'done' && report && (
              <div className="space-y-4">
                <div
                  className="space-y-3"
                  role="list"
                  aria-label="Resultados da verificação"
                >
                  {visibleChecks?.map(check => (
                    <CheckItem key={check.checkId} check={check} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="min-w-0 space-y-4">
            {!isFiscalMonitoring && (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                <p className="text-sm font-semibold text-slate-900">
                  Monitoramento e câmera
                </p>
                <p className="mt-2 text-sm text-slate-600">
                  {needsCamera
                    ? 'A prova exige câmera e áudio ativos com pré-visualização.'
                    : 'Este tipo de prova não exige pré-visualização de câmera nesta etapa.'}
                </p>

                <div className="mt-4 flex min-h-[180px] items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white p-4">
                  {previewStream ? (
                    <VideoPreview
                      height="100%"
                      width="100%"
                      stream={previewStream}
                      className="max-h-[240px] w-full rounded-xl"
                    />
                  ) : (
                    <p className="max-w-xs text-center text-sm text-slate-500">
                      A pré-visualização aparecerá aqui após liberar câmera e
                      áudio.
                    </p>
                  )}
                </div>

                {errorMessage && (
                  <p className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {errorMessage}
                  </p>
                )}

                {!errorMessage && lightingWarning && (
                  <p className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                    {lightingWarning}
                  </p>
                )}
              </div>
            )}

            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="grid gap-3 rounded-xl bg-slate-900 p-4 text-white sm:grid-cols-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-slate-400">
                    Bloqueios
                  </p>
                  <p className="mt-1 text-xl font-semibold">
                    {blockingChecks?.length ?? 0}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-slate-400">
                    Avisos
                  </p>
                  <p className="mt-1 text-xl font-semibold">
                    {warningChecks?.length ?? 0}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-slate-400">
                    Mídia
                  </p>
                  <p className="mt-1 text-sm font-semibold">
                    {mediaStatusLabel}
                  </p>
                </div>
              </div>

              <div className="mt-4 space-y-3">
                {onBack && (
                  <button
                    type="button"
                    onClick={onBack}
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
                  >
                    Voltar
                  </button>
                )}

                {state !== 'done' && (
                  <button
                    type="button"
                    onClick={handleStart}
                    disabled={state === 'running'}
                    className={`w-full rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-800 transition hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60 ${
                      !hasStarted && state !== 'running'
                        ? 'cta-guidance cta-guidance-blue'
                        : ''
                    }`}
                  >
                    {state === 'running'
                      ? 'Preparando...'
                      : 'Iniciar preparação'}
                  </button>
                )}

                {state === 'done' && report?.overallStatus !== 'approved' && (
                  <button
                    type="button"
                    onClick={() => {
                      handleRetry().catch(() => undefined);
                    }}
                    disabled={retrying}
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {retrying
                      ? 'Revalidando...'
                      : 'Corrigir e validar novamente'}
                  </button>
                )}

                <button
                  type="button"
                  onClick={handleContinue}
                  disabled={!canContinue}
                  className={`w-full rounded-xl bg-green-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50 ${
                    canContinue ? 'cta-guidance cta-guidance-green' : ''
                  }`}
                >
                  {resolvedMonitoringType === 'fiscal'
                    ? 'Continuar para autorização fiscal'
                    : 'Iniciar prova'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
